import { clinicalInstant, clinicalTimeCandidates, clinicalDifferenceSeconds } from './clinicalTime';
import { isCriticalTimeUnknown } from './unknownCriticalTimes';
import { toLocalDateTime } from './birthSummaryUtils';

const common = [['pushingStart', 'Pushing Start']];
const cephalic = [['crowning', 'Crowning'], ['headBornTime', 'Head Born At']];
const breech = [
  ['buttocksVisible', 'Rump Visible'], ['buttocksBorn', 'Rump Born'],
  ['trunkToUmbilicus', 'Umbilicus'], ['trunkToAxillae', 'Axillae'],
  ['armsDelivered', 'Arms Delivered'], ['headVisible', 'Head Visible']
];
const after = [['bodyBornTime', 'Body Born At'], ['firstBreathTime', 'First Breath']];
const read = (value, name) => value?.[name] ?? value?.[name[0].toUpperCase() + name.slice(1)];

export const getBirthChronologyError = (birthEvent = {}, { requireResolved = false } = {}) => {
  const critical = read(birthEvent, 'criticalTimes');
  const breechTimes = read(birthEvent, 'breechTimes');
  const fields = [...common, ...(read(birthEvent, 'birthPresentation') === 'Breech' ? breech : cephalic), ...after];
  let previous;
  for (const [field, label] of fields) {
    if (isCriticalTimeUnknown(birthEvent, field)) continue;
    const raw = read(birthEvent, field) ?? read(critical, field) ?? read(breechTimes, field);
    if (!raw) continue; // Drafts may omit times, but recorded times must agree.
    const local = toLocalDateTime(raw);
    if (!local) return `${label} must include a valid date and time.`;
    const instant = clinicalInstant(raw);
    const value = local.length === 16 ? `${local}:00` : local;
    let precedes = previous && value < previous.value;
    if (previous && (instant !== null || previous.instant !== null)) {
      const candidates = instant === null ? clinicalTimeCandidates(raw) : [raw];
      const priorCandidates = previous.instant === null ? clinicalTimeCandidates(previous.raw) : [previous.raw];
      if (candidates.length !== 1 || priorCandidates.length !== 1) {
        if (requireResolved) return 'Confirm whether the recorded times occurred before or after the clock change.';
        previous = { value, label, instant, raw };
        continue; // The save boundary asks for the missing occurrence before validation.
      }
      precedes = Date.parse(candidates[0]) < Date.parse(priorCandidates[0]);
    }
    if (precedes) {
      return `${label} cannot be before ${previous.label}. Check both dates for an overnight birth.`;
    }
    previous = { value, label, instant, raw };
  }
  return null;
};

export const getLaborEpisodeChronologyError = (episode, options) => {
  const babies = read(episode, 'babies') || [];
  // Singletons share the primary event; the baby copy may still contain loaded times.
  // Multiples store each birth's critical times on its own child event.
  if (babies.length <= 1) return getBirthChronologyError(read(episode, 'primaryBirthEvent'), options);
  for (const [index, entry] of babies.entries()) {
    const error = getBirthChronologyError(read(entry, 'birthEvent'), options);
    if (error) return `Baby ${index + 1}: ${error}`;
  }
  return null;
};


export const getNewbornMedicationTimingWarnings = episode => {
  const babies = read(episode, 'babies') || [];
  return babies.flatMap((entry, index) => {
    const birth = read(entry, 'birthEvent') || {};
    const primary = babies.length === 1 ? read(episode, 'primaryBirthEvent') : birth;
    const born = read(primary, 'bodyBornTime') || read(read(primary, 'criticalTimes'), 'bodyBornTime') || read(birth, 'birthDateTime');
    return [['vitaminKTime', 'Vitamin K'], ['eyeOintmentTime', 'Eye ointment']].flatMap(([field, label]) => {
      const difference = clinicalDifferenceSeconds(born, read(birth, field));
      return difference !== null && difference < 0
        ? [`${babies.length > 1 ? `Baby ${index + 1}: ` : ''}${label} is charted before birth. Review its date, time, and clock occurrence.`] : [];
    });
  });
};
